import {
  Avatar,
  Chip,
  Divider,
  Grid,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import useStyles from "./styles";

export default function ModalDetails(params) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const {
    openDetalhes = false,
    setOpenDetalhes = () => {},
    selectedRow = {},
  } = params;

  // Lista de campos que não deverão ser exibidos na renderição genérica
  const camposOcultos = [
    "id",
    "createAt",
    "updatedAt",
    "clienteId",
    "status",
    "plano",
    "diaDePagamento",
    "tipo",
    "cliente",
    "createdAt",
    "usuarioId",
    "foto",
    "logsLoginClienteId",
    "usuarioClienteId",
    "logsLoginId",
    "resource",
    "router",
    "action",
    "previousData",
    "newData",
    "time",
    "session",
    "user",
    "usuarioCliente",
    "logsLoginCliente",
    "usuario",
    "logsLogin",
  ];

  // Função para extrair dados do usuário baseado na estrutura do JSON
  const getUserData = () => {
    // Verifica se existe usuarioCliente (para logs de cliente)
    if (selectedRow?.usuarioCliente) {
      return {
        nome: selectedRow.usuarioCliente.perfilCliente?.[0]?.nome || "",
        sobrenome:
          selectedRow.usuarioCliente.perfilCliente?.[0]?.sobrenome || "",
        foto: selectedRow.usuarioCliente.perfilCliente?.[0]?.foto || "",
        navegador: selectedRow.logsLoginCliente?.navegador || "",
        sistema: selectedRow.logsLoginCliente?.sistemaOperacional || "",
        ip: selectedRow.logsLoginCliente?.enderecoIp || "",
        time: selectedRow.time || "",
      };
    }

    // Verifica se existe usuario (para logs interno)
    if (selectedRow?.usuario) {
      return {
        nome: selectedRow.usuario.perfil?.[0]?.nome || "",
        sobrenome: selectedRow.usuario.perfil?.[0]?.sobrenome || "",
        foto: selectedRow.usuario.perfil?.[0]?.foto || "",
        navegador: selectedRow.logsLogin?.navegador || "",
        sistema: selectedRow.logsLogin?.sistemaOperacional || "",
        ip: selectedRow.logsLogin?.enderecoIp || "",
        time: selectedRow.time || "",
      };
    }

    // Fallback para dados diretos
    return {
      nome: selectedRow.user?.nome || selectedRow.usuario?.nome || "",
      sobrenome:
        selectedRow.user?.sobrenome || selectedRow.usuario?.sobrenome || "",
      foto: selectedRow.user?.foto || selectedRow.usuario?.foto || "",
      navegador:
        selectedRow.session?.navegador ||
        selectedRow.logsLogin?.navegador ||
        "",
      sistema:
        selectedRow.session?.sistemaOperacional ||
        selectedRow.logsLogin?.sistemaOperacional ||
        "",
      ip:
        selectedRow.session?.enderecoIp ||
        selectedRow.logsLogin?.enderecoIp ||
        "",
      time: selectedRow.time || "",
    };
  };

  const userData = getUserData();

  // Função para obter dados de "from" e "to" baseado na estrutura
  const getChangeData = () => {
    // Para ações de edição
    if (selectedRow.action === "Edição") {
      return {
        from: selectedRow.previousData || {},
        to: selectedRow.newData || {},
      };
    }

    // Para ações de criação
    if (selectedRow.action === "Criação") {
      return {
        from: null,
        to: selectedRow.newData || {},
      };
    }

    // Para ações de exclusão
    if (selectedRow.action === "Exclusão") {
      return {
        from: selectedRow.previousData || {},
        to: null,
      };
    }

    return { from: {}, to: {} };
  };

  const changeData = getChangeData();

  function getKeys(obj) {
    if (!obj) return [];

    return Object.keys(obj).flatMap((k) => {
      return typeof obj[k] == "object" && obj[k] !== null
        ? getKeys(obj[k] || {})
        : { [k]: obj[k] };
    });
  }

  // Prepara dados flat para exibição
  let flatFrom = [];
  let flatTo = [];

  if (changeData.from) {
    const flatObj = getKeys(changeData.from);
    flatFrom = [Object.assign({}, ...flatObj)];
  }

  if (changeData.to) {
    const flatObj = getKeys(changeData.to);
    flatTo = [Object.assign({}, ...flatObj)];
  }

  // Divide string por letras maiúsculas
  const wordRegex = /[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])/g;

  const formatLabel = (key) => {
    return (
      key[0].toUpperCase() + key.substring(1).match(wordRegex)?.join(" ") ||
      key.substring(1)
    );
  };

  return (
    <Modal
      open={openDetalhes}
      onClose={() => {
        setOpenDetalhes(false);
      }}
      sx={styles.modal}
    >
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          mx: 3,
          maxWidth: "900px",
          maxHeight: "80vh",
          borderRadius: 3,
          p: 3,
        }}
      >
        <Grid container p={1} spacing={1}>
          <Grid item xs={2}>
            <Avatar
              src={userData?.foto}
              sx={{
                justifySelf: "center",
                width: "100%",
                height: "auto",
                maxWidth: "80px",
                minWidth: "40px",
                aspectRatio: "1 / 1",
                objectFit: "cover",
                mr: 1,
              }}
            />
          </Grid>

          <Grid container xs={10} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="title">
                {userData?.nome} {userData?.sobrenome}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={styles.headerSubtitle}>
                Navegador
              </Typography>
              <Typography sx={styles.headerText}>
                {userData?.navegador}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={styles.headerSubtitle}>
                Sistema
              </Typography>
              <Typography sx={styles.headerText}>
                {userData?.sistema}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={styles.headerSubtitle}>
                IP
              </Typography>
              <Typography sx={styles.headerText}>{userData?.ip}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={styles.headerSubtitle}>
                Hora
              </Typography>
              <Typography sx={styles.headerText}>{userData?.time}</Typography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={6} justifyContent={"flex-start"}>
            {changeData?.from ? (
              <Typography>
                {selectedRow?.action === "Edição"
                  ? "De: "
                  : "Dados Removidos: "}
              </Typography>
            ) : (
              <Typography>{"Dados Criados: "}</Typography>
            )}
          </Grid>
          <Grid item xs={6} textAlign={"right"}>
            <Chip
              sx={{
                height: "24px",
                bgcolor:
                  selectedRow?.action === "Criação"
                    ? "success.main"
                    : selectedRow?.action === "Edição"
                    ? "secondary.main"
                    : "error.main",
                color: theme.palette.white.main,
              }}
              label={selectedRow?.action}
            />
          </Grid>
          {flatFrom?.length > 0 &&
            flatFrom?.map((item, index) =>
              Object?.keys(item)?.map(
                (key) =>
                  !camposOcultos?.includes(key) && (
                    <Grid item xs={6} md={4} lg={3}>
                      <Tooltip title={item[key]} arrow>
                        <TextField
                          fullWidth
                          key={`from-${key}-${index}`}
                          variant="outlined"
                          label={formatLabel(key)}
                          value={item[key]}
                          size="small"
                          disabled={true}
                        />
                      </Tooltip>
                    </Grid>
                  )
              )
            )}
          {changeData?.to && selectedRow?.action !== "Exclusão" && (
            <>
              {selectedRow?.action === "Edição" && (
                <Grid item xs={12} justifyContent="flex-start">
                  <Typography>{"Para:"}</Typography>
                </Grid>
              )}
              {flatTo.map((item, index) =>
                Object.keys(item)?.map(
                  (key) =>
                    !camposOcultos?.includes(key) && (
                      <Grid
                        item
                        xs={6}
                        md={4}
                        lg={3}
                        key={`to-${key}-${index}`}
                      >
                        <Tooltip title={item[key]} arrow>
                          <TextField
                            variant="outlined"
                            label={formatLabel(key)}
                            value={item[key]}
                            size="small"
                            fullWidth
                            disabled={true}
                          />
                        </Tooltip>
                      </Grid>
                    )
                )
              )}
            </>
          )}
        </Grid>

        {/* </Box> */}
      </Paper>
    </Modal>
  );
}
